// @flow
import React from 'react';
import classNames from 'classnames/bind';
import { Container, Row, Col } from 'Components/Grid';
import { Watch } from 'scrollmonitor-react';
import { formatPrice, stripTags } from 'Utils';
import Price from 'Components/Price';
import Button from 'Components/Button';
import OfferModel from 'Stores/OfferModel';
import styles from './styles.scss';
import { getButtonLabel } from './getButtonLabel';
import CallToAction from 'Components/CallToAction';
import ConsumptionData from 'Components/ConsumptionData';
import { lowerCase6e } from 'Utils/special/lowerCaseSpecial';
import Typography from 'Components/Typography';

type Props = {
  offer: OfferModel,
  index: number,
  isInViewport: boolean,
  stopWatcher: () => void,
  onOpenContact: () => void,
  hasForm: String,
};

type State = {};

class OfferEntryPrivate extends React.Component<Props, State> {
  // @see https://github.com/facebook/react/issues/12670
  state = {};

  static getDerivedStateFromProps(props: Props) {
    if (props.isInViewport) {
      props.stopWatcher();
    }

    return null;
  }

  pricePrefix(value: string) {
    return (
      <Typography.Headline
        className={styles.priceTitle}
        forceUppercase={true}
        level={'h4'}
        value={value}
      />
    );
  }

  get cashPricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Für' : 'Ab';

    if (offer.cashPrice && offer.cashPrice.pricePrefix) {
      text = offer.cashPrice.pricePrefix;
    }

    return this.pricePrefix(text);
  }

  get leasingPricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Leasing für' : 'Leasing ab';

    if (offer.leasingPrice && offer.leasingPrice.pricePrefix) {
      text = offer.leasingPrice.pricePrefix;
    }

    return this.pricePrefix(text);
  }

  get financePricePrefix() {
    const { offer } = this.props;
    let text = offer.offerSubtype === 'VOLUME' ? 'Finanzierung für' : 'Finanzierung ab';

    if (offer.financePrice && offer.financePrice.pricePrefix) {
      text = offer.financePrice.pricePrefix;
    }

    return this.pricePrefix(text);
  }

  render() {
    const { offer, isInViewport, background, alternateColors, id, hasForm, onOpenContact } =
      this.props;

    const className = classNames(styles.entry, {
      [background]: true,
      [styles.private]: true,
      [styles.isInViewport]: window.isPreview || isInViewport,
      [styles.alternateColors]: alternateColors,
    });

    return (
      <div className={className} id={id}>
        <Container>
          <Row>
            <Col>
              <Typography.Headline
                className={styles.title}
                forceUppercase={true}
                level={'h3'}
                value={lowerCase6e(offer.modelDescription)}
                additionalProps={{ 'data-e2e': 'offer-modelDescription' }}
                additionalAriaLabelText={offer.consumptionInfoDescription}
              />
            </Col>
          </Row>
          {offer.cashPrice && offer.cashPrice.price > 0 ? (
            <Row>
              <Col>
                <div className={styles.cashPrice} data-e2e="offer-cashPrice">
                  {this.cashPricePrefix}
                  {formatPrice(offer.cashPrice.price)}
                  <span
                    data-e2e="offer-cashPrice-priceText"
                    dangerouslySetInnerHTML={{ __html: offer.cashPrice.priceText }}
                  />
                </div>
              </Col>
            </Row>
          ) : null}
          {offer.consumptionInfoDescription && (
            <>
              <Row className={styles.imageRow}>
                <Col cols={12} m={6} e2e="offer-consumptionInfoDescription">
                  <ConsumptionData
                    consumptionText={[offer.consumptionInfoDescription]}
                    inside="offer"
                    hideCloseButton
                    ariaHidden={true}
                  />
                </Col>
              </Row>
              <br />
            </>
          )}
          <Row align="center" className={styles.imageRow}>
            <Col cols={12} m={6}>
              <div
                data-e2e="offer-description"
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: offer.description }}
              />
              {offer.offerSubtype === 'VOLUME' &&
              offer.customerBenefitPrice &&
              offer.customerBenefitPrice > 0 ? (
                <span className={styles.customerBenefit} data-e2e="offer-customerBenefit">
                  {offer.customerBenefitPriceText && (
                    <span
                      data-e2e="offer-customerBenefitPriceText"
                      dangerouslySetInnerHTML={{ __html: `${offer.customerBenefitPriceText} ` }}
                    ></span>
                  )}
                  {formatPrice(offer.customerBenefitPrice)}
                  {offer.customerBenefitPriceFootnote && (
                    <span
                      data-e2e="offer-customerBenefitPriceFootnote"
                      dangerouslySetInnerHTML={{ __html: offer.customerBenefitPriceFootnote }}
                    ></span>
                  )}
                </span>
              ) : null}
            </Col>

            <Col cols={12} m={6}>
              <div
                className={styles.image}
                style={
                  isInViewport || window.isPreview
                    ? { backgroundImage: `url(${offer.image.src}) ` }
                    : undefined
                }
              />
            </Col>
          </Row>
          <Row align="center">
            {offer.financePrice && offer.financePrice.price > 0 && (
              <Col align="center" m={4}>
                <div className={styles.priceWrapper} data-e2e="financePricePrefix">
                  {this.financePricePrefix}
                  <Price value={formatPrice(offer.financePrice.price)} />
                  <p
                    className={styles.priceText}
                    dangerouslySetInnerHTML={{ __html: offer.financePrice.priceText }}
                  />
                </div>
              </Col>
            )}

            {offer.leasingPrice &&
              offer.financePrice &&
              offer.leasingPrice.price > 0 &&
              offer.financePrice.price > 0 && (
                <Col align="center" m={2}>
                  <div className={styles.hr} />
                </Col>
              )}

            {offer.leasingPrice && offer.leasingPrice.price > 0 && (
              <Col align="center" m={4}>
                <div className={styles.priceWrapper} data-e2e="leasingPricePrefix">
                  {this.leasingPricePrefix}
                  <Price value={formatPrice(offer.leasingPrice.price)} />
                  <p
                    className={styles.priceText}
                    dangerouslySetInnerHTML={{ __html: offer.leasingPrice.priceText }}
                  />
                </div>
              </Col>
            )}
          </Row>
          <Row align="center" className={styles.buttonRow}>
            <Col m={10} align="center">
              {offer.linkUrl1 && offer.linkTitle1 ? (
                <Button label={offer.linkTitle1} variant="primary" href={offer.linkUrl1} />
              ) : (
                <CallToAction
                  label={getButtonLabel(hasForm)}
                  mazdaForm={!hasForm}
                  model={offer.carlineDeeplink}
                  onOpenContact={onOpenContact}
                  type="REQUEST_FOR_PROPOSAL"
                  small
                />
              )}
            </Col>

            {offer.linkUrl && (
              <Col m={10} align="center" className={styles.buttonSecondary}>
                <Button
                  label={offer.linkTitle || 'Direkt verfügbare Modelle'}
                  variant="secondary"
                  href={offer.linkUrl}
                />
              </Col>
            )}
          </Row>
        </Container>
      </div>
    );
  }
}

export default Watch(OfferEntryPrivate);
